import { useState, useEffect } from 'react';
import { Shield, AlertTriangle, Gavel, ShoppingCart, Lock } from 'lucide-react';
import { Button } from './ui/button';
import { api, formatApiError } from '../lib/api';
import { toast } from 'sonner';

const ACTION_ICONS = {
  purchase: ShoppingCart,
  reserve: Lock,
  bid: Gavel,
  protected_purchase: Shield,
};

const ACTION_COLORS = {
  purchase: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
  reserve: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
  bid: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
  protected_purchase: 'text-purple-400 bg-purple-500/10 border-purple-500/20',
};

export function BuyerCommitmentModal({ isOpen, onClose, onConfirm, actionType, listingId, auctionId }) {
  const [loading, setLoading] = useState(false);
  const [commitmentText, setCommitmentText] = useState(null);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    if (isOpen && actionType) {
      api.getCommitmentText(actionType)
        .then(setCommitmentText)
        .catch(() => toast.error('Error cargando términos'));
    }
  }, [isOpen, actionType]);

  useEffect(() => {
    if (!isOpen) setAccepted(false);
  }, [isOpen]);

  const handleConfirm = async () => {
    if (!accepted) return;
    setLoading(true);
    try {
      const fingerprint = navigator.userAgent + '|' + screen.width + 'x' + screen.height + '|' + new Date().getTimezoneOffset();
      await api.recordCommitment({
        listing_id: listingId || null,
        auction_id: auctionId || null,
        action_type: actionType,
        fingerprint: btoa(fingerprint).substring(0, 64),
        modal_version: '1.0',
      });
      onConfirm();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !commitmentText) return null;

  const Icon = ACTION_ICONS[actionType] || Shield;
  const colorClass = ACTION_COLORS[actionType] || ACTION_COLORS.purchase;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="commitment-modal">
      <div className="bg-[#121215] border border-white/10 rounded-2xl max-w-md w-full shadow-2xl overflow-hidden">
        {/* Header */}
        <div className={`p-6 border-b border-white/5 flex items-center gap-4`}>
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center border ${colorClass}`}>
            <Icon className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-zinc-100" data-testid="commitment-title">
              {commitmentText.title}
            </h2>
            <span className="text-xs text-zinc-500">v{commitmentText.version}</span>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-5">
          <div className="p-4 rounded-xl bg-white/[0.03] border border-white/5">
            <p className="text-sm text-zinc-300 leading-relaxed" data-testid="commitment-text">
              {commitmentText.text}
            </p>
          </div>

          <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-500/5 border border-amber-500/10">
            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-300/80">
              Este compromiso queda registrado con tu IP, fingerprint y timestamp para fines legales y de disputas.
            </p>
          </div>

          <label className="flex items-start gap-3 cursor-pointer group" data-testid="commitment-checkbox-label">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-1 rounded border-white/20 bg-white/5 text-purple-500 focus:ring-purple-500/30"
              data-testid="commitment-checkbox"
            />
            <span className="text-sm text-zinc-400 group-hover:text-zinc-300 transition-colors">
              He leído y acepto los términos de este compromiso
            </span>
          </label>
        </div>

        {/* Footer */}
        <div className="p-6 pt-0 flex gap-3">
          <Button
            variant="outline"
            className="flex-1 border-white/10 text-zinc-400"
            onClick={onClose}
            disabled={loading}
            data-testid="commitment-cancel-btn"
          >
            Cancelar
          </Button>
          <Button
            className="flex-1 bg-zinc-100 text-zinc-900 hover:bg-white disabled:opacity-40"
            onClick={handleConfirm}
            disabled={!accepted || loading}
            data-testid="commitment-confirm-btn"
          >
            {loading ? 'Registrando...' : 'Confirmar compromiso'}
          </Button>
        </div>
      </div>
    </div>
  );
}
